import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Search, X, Calendar, Users, Clock, MessageCircle, Ban, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CONTACTS } from "@/data/contacts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  confirmed: { label: "Confermata", color: "text-emerald-300", bg: "bg-emerald-500/15 border-emerald-500/30" },
  pending: { label: "In attesa di conferma", color: "text-amber-300", bg: "bg-amber-500/15 border-amber-500/30" },
  cancelled: { label: "Annullata", color: "text-red-300", bg: "bg-red-500/15 border-red-500/30" },
};

export default function ManageReservationDialog({ open, onOpenChange }) {
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const reset = () => { setCode(""); setPhone(""); setReservation(null); };

  const search = async (e) => {
    e.preventDefault();
    if (!code.trim() || !phone.trim()) { toast.error("Inserisci codice e telefono."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/reservations/lookup`, { code: code.trim(), phone: phone.trim() });
      setReservation(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Prenotazione non trovata.";
      toast.error(typeof msg === "string" ? msg : "Errore ricerca.");
    } finally {
      setLoading(false);
    }
  };

  const cancel = () => {
    if (!reservation) return;
    const dateFmt = format(new Date(reservation.date), "d MMM yyyy", { locale: it });
    const msg = `Ciao Drago Ramen 🐉\n\nVorrei *annullare* la mia prenotazione:\n\n*Codice:* ${reservation.id.slice(0,8).toUpperCase()}\n*Nome:* ${reservation.name} ${reservation.surname}\n*Data:* ${dateFmt} ore ${reservation.time}\n*Persone:* ${reservation.people}\n\nGrazie mille!`;
    window.location.href = `https://wa.me/${CONTACTS.cellIntl}?text=${encodeURIComponent(msg)}`;
  };

  const modifyOnWhatsApp = () => {
    if (!reservation) return;
    const dateFmt = format(new Date(reservation.date), "d MMM yyyy", { locale: it });
    const msg = `Ciao Drago Ramen 🐉\n\nVorrei *modificare* la mia prenotazione:\n\n*Codice:* ${reservation.id.slice(0,8).toUpperCase()}\n*Nome:* ${reservation.name} ${reservation.surname}\n*Data attuale:* ${dateFmt} ore ${reservation.time}\n*Persone:* ${reservation.people}\n\nMi servirebbe:`;
    window.open(`https://wa.me/${CONTACTS.cellIntl}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const meta = reservation ? (STATUS_META[reservation.status] || STATUS_META.pending) : null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-ink border-white/10 text-white max-w-md p-0 overflow-hidden" data-testid="manage-reservation-dialog">
        <DialogHeader className="p-6 pb-4 border-b border-white/5">
          <DialogTitle className="text-white font-display text-2xl">
            {reservation ? "La tua prenotazione" : "Gestisci prenotazione"}
          </DialogTitle>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {!reservation ? (
            <motion.form
              key="lookup"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onSubmit={search}
              className="p-6 space-y-4"
              data-testid="manage-lookup-form"
            >
              <p className="text-sm text-white/60 leading-relaxed">
                Inserisci il <span className="text-gold">codice prenotazione</span> ricevuto e il numero di telefono con cui hai prenotato.
              </p>
              <label className="block">
                <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Codice</span>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="es. A7B3D2F1"
                  maxLength={8}
                  data-testid="manage-code-input"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/35 outline-none focus:border-gold font-mono uppercase transition-colors"
                />
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Telefono</span>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+39 344..."
                  inputMode="tel"
                  data-testid="manage-phone-input"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/35 outline-none focus:border-gold transition-colors"
                />
              </label>
              <button
                type="submit"
                disabled={loading}
                data-testid="manage-search-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-ember hover:bg-emberDeep disabled:opacity-60 text-white px-6 py-3 rounded-full font-medium transition-colors"
              >
                {loading ? <><Loader2 size={16} className="animate-spin" /> Cerco...</> : <><Search size={15} /> Cerca prenotazione</>}
              </button>
              <p className="text-xs text-white/40 text-center pt-1">
                Preferisci parlare con noi? <a href={`https://wa.me/${CONTACTS.cellIntl}`} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Scrivici su WhatsApp</a>
              </p>
            </motion.form>
          ) : (
            <motion.div
              key="details"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="p-6 space-y-5"
              data-testid="manage-reservation-details"
            >
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs ${meta.bg} ${meta.color}`} data-testid="manage-status">
                {reservation.status === "confirmed" && <Check size={12} />}
                {reservation.status === "cancelled" && <Ban size={12} />}
                {meta.label}
              </div>

              <div>
                <div className="text-xs text-white/40 uppercase tracking-widest">Codice</div>
                <div className="mt-1 font-mono text-white text-lg">{reservation.id.slice(0, 8).toUpperCase()}</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <InfoBox icon={<Calendar size={14} />} label="Data" value={format(new Date(reservation.date), "d MMM yyyy", { locale: it })} />
                <InfoBox icon={<Clock size={14} />} label="Orario" value={reservation.time} />
                <InfoBox icon={<Users size={14} />} label="Persone" value={reservation.people} />
                <InfoBox icon={<span>👤</span>} label="Nome" value={`${reservation.name} ${reservation.surname}`} />
              </div>

              {reservation.notes && (
                <div className="rounded-xl bg-ash/60 border border-white/5 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Note</div>
                  <div className="text-sm text-white/80">{reservation.notes}</div>
                </div>
              )}

              {reservation.status !== "cancelled" && (
                <div className="grid grid-cols-1 gap-2 pt-2">
                  <button
                    onClick={modifyOnWhatsApp}
                    data-testid="manage-modify-wa-btn"
                    className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FBA57] text-white px-6 py-3 rounded-full text-sm font-medium transition-colors"
                  >
                    <MessageCircle size={15} /> Chiedi modifica via WhatsApp
                  </button>
                  <button
                    onClick={cancel}
                    data-testid="manage-cancel-btn"
                    className="inline-flex items-center justify-center gap-2 border border-red-500/40 hover:bg-red-500/10 text-red-300 hover:text-red-200 px-6 py-3 rounded-full text-sm transition-colors"
                  >
                    <Ban size={14} /> Annulla via WhatsApp
                  </button>
                </div>
              )}

              <button
                onClick={reset}
                data-testid="manage-reset-btn"
                className="w-full text-xs text-white/50 hover:text-white transition-colors pt-2"
              >
                ← Cerca un'altra prenotazione
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

function InfoBox({ icon, label, value }) {
  return (
    <div className="rounded-xl bg-ash/60 border border-white/5 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/50">
        <span className="text-gold">{icon}</span> {label}
      </div>
      <div className="mt-1 text-sm text-white">{value}</div>
    </div>
  );
}
